import { useEffect, useState, useRef } from "react";
import { Outlet, useNavigate, useMatch, useLocation } from "react-router";
import Swal from "sweetalert2";

// Actions
import { requestPause, setScheduledPause } from "src/store/auth/reducer";
import { userLogout as logout } from "src/store/auth/actions";
import { doPausePaRequest, doUnpausePaRequest } from "src/store/context/actions";
import { setQueues } from "src/store/config/reducer";

// Hooks
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import { createWsClient } from "src/services/ws";

// Components
import Sidebar from "./components/sidebar";
import Aside from "./components/aside";

// Constants
import { AppStatus, togglePause } from "src/store/context/reducer";

// Queue
import { ws } from "src/services/ws";
import type { PauseType } from "src/types";

// Components
import ScreenLoader from "src/components/screen-loader";

// Services
import { initDeviceManager } from "src/services/media-manager";
import { MediaManagerInstance } from "src/services/media-manager";
import userRegistration from "./workflows/user-registration";
import { stopNetworkMonitor } from "src/services/network-monitor";

export default function Main() {
  // Access Redux
  const location = useLocation();
  const { appStatus } = useAppSelector((state) => state.context);
  const { isLoggedIn, manualLogout, paused, scheduledPause, appWasLoaded, userInfo } = useAppSelector((state) => state.auth);
  const { inputDeviceId, cameraDeviceId, outputDeviceId } = useAppSelector((state) => state.media);
  const { sip, queues, general } = useAppSelector((state) => state.config);
  const appliedTheme = general.theme;

  // Create media manager
  initDeviceManager();

  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isCallScreen = useMatch("/call");

  // Inner State
  const [state, setState] = useState<{
    fullAppLoad: boolean;
    ws: Awaited<ReturnType<typeof createWsClient>> | null;
  }>({
    fullAppLoad: false,
    ws: null,
  });

  const currentPauseStatus = useRef(paused);
  const wsBinds: Array<number> = [];
  const waitingTransferTimer = useRef<number | null>(null);

  const inScreenCall = location.pathname === "/call" || appStatus === AppStatus.IN_CALL;

  // Methods
  const pausePa = async (requestedPause: PauseType) => {
    if (waitingTransferTimer.current) clearTimeout(waitingTransferTimer.current);

    const { success, message } = await doPausePaRequest(requestedPause.id);

    if (success) {
      // Update Queue
      ws?.sendMessage({
        type: "PA_SET_STATUS_PAUSE",
        data: { pauseType: requestedPause.texto },
      });

      // UPDATE MAIN APP STATUS
      dispatch(togglePause(requestedPause));

      // Save actual pause
      currentPauseStatus.current = requestedPause;

      // CLEAR TIMER
    } else alert("Erro ao aplicar pausa: " + message);
  };

  const unPausePa = async () => {
    const { success, message } = await doUnpausePaRequest();

    dispatch(togglePause(false));

    // Update Queue
    ws?.sendMessage({
      type: "PA_SET_STATUS_AVAILABLE",
      data: {
        isWaitingTransfer: false,
      },
    });

    // Clear current pause status
    currentPauseStatus.current = null;
  };

  const onWsChangePaQueus = async (eventData: any) => {
    const queues = userInfo?.hasMorgaCertificate ? eventData.queues : eventData.queues.filter((q: string) => q.toLowerCase().indexOf("abr") === -1);

    dispatch(setQueues(queues));
    ws.connect();
  };

  // Set audio output
  useEffect(() => {
    type AudioEl = HTMLAudioElement & {
      setSinkId?: (sinkId: string) => Promise<void>;
    };

    const trackedAudios = new Set<AudioEl>();

    async function applySink(audio: AudioEl) {
      if (typeof audio.setSinkId !== "function") return;

      try {
        if (audio.readyState > 0) {
          await audio.setSinkId(outputDeviceId);
        }
      } catch (error) {
        console.error("Erro ao aplicar sinkId no audio:", error);
      }
    }

    async function handleLoadedMetadata(event: Event) {
      const audio = event.currentTarget as AudioEl;

      if (typeof audio.setSinkId !== "function") return;

      try {
        await audio.setSinkId(outputDeviceId);
      } catch (error) {
        console.error("Erro ao aplicar sinkId no loadedmetadata:", error);
      }
    }

    function attachAudio(audio: AudioEl) {
      if (trackedAudios.has(audio)) return;

      trackedAudios.add(audio);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      void applySink(audio);
    }

    function detachAudio(audio: AudioEl) {
      if (!trackedAudios.has(audio)) return;

      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      trackedAudios.delete(audio);
    }

    function collectAudiosFromNode(node: Node): AudioEl[] {
      const audios: AudioEl[] = [];

      if (!(node instanceof Element)) return audios;

      if (node.tagName === "AUDIO") {
        audios.push(node as AudioEl);
      }

      node.querySelectorAll("audio").forEach((audio) => {
        audios.push(audio as AudioEl);
      });

      return audios;
    }

    // registra os já existentes
    document.querySelectorAll("audio").forEach((audio) => {
      attachAudio(audio as AudioEl);
    });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          const audios = collectAudiosFromNode(node);
          audios.forEach(attachAudio);
        });

        mutation.removedNodes.forEach((node) => {
          const audios = collectAudiosFromNode(node);
          audios.forEach(detachAudio);
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();

      trackedAudios.forEach((audio) => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      });

      trackedAudios.clear();
    };
  }, [outputDeviceId]);

  // Validate loggin
  useEffect(() => {
    if (!isLoggedIn) {
      ws?.disconnect();
      navigate("/login", {
        state: {
          authError: !manualLogout && "Sua sessão expirou, faça login novamente!",
        },
      });
    }
  }, [isLoggedIn]);

  // Monitor app status
  useEffect(() => {
    async function onAppStatusChange() {
      if (!isCallScreen && appStatus === AppStatus.IN_CALL) return navigate("/call");

      if (appStatus === AppStatus.IDLE) {
        // Apply pause if scheduled
        if (scheduledPause) {
          dispatch(requestPause(scheduledPause));
          return dispatch(setScheduledPause(null));
        }
      }
    }
    onAppStatusChange();
  }, [appStatus]);

  // Pause Manager
  useEffect(() => {
    async function toggle() {
      // Pause
      if (paused) {
        pausePa(paused);
      } else {
        unPausePa();
      }
    }

    if (state.fullAppLoad) toggle();
  }, [paused]);

  // Init first init
  useEffect(() => {
    async function initApp() {
      const references = {
        waitingTransferTimer,
        currentPauseStatus,
      };

      const reduxData = {
        appWasLoaded,
        paused,
        cameraDeviceId,
        dispatch,
        inputDeviceId,
        queues,
        sip,
      };

      const methods = {
        pausePa,
      };

      const { success, data, message } = await userRegistration(references, reduxData, methods);

      if (!success) {
        if (isLoggedIn)
          await Swal.fire({
            title: "Erro ao inicializar aplicação",
            text: message,
            icon: "error",
            confirmButtonText: "Sair e tentar novamente",
          });

        return dispatch(logout(true));
      }

      // Binds
      // wsBinds.push(ws?.on("CHANGE_PA_QUEUES", onWsChangePaQueus));

      // Set app was loaded
      return setState((s) => ({ ...s, fullAppLoad: true, ws: data.ws }));
    }

    async function onBeforeUnload(e: BeforeUnloadEvent) {
      console.log("Before Unload");

      if (ws) ws.disconnect();
      stopNetworkMonitor();

      return delete e["returnValue"];
    }

    // Before unload event
    window.addEventListener("beforeunload", onBeforeUnload);

    if (navigator.platform.match(/Mac/i)) {
      document.body.classList.add("macos");
    }

    // Start bindings
    initApp();

    return () => {
      // Stop tracks
      MediaManagerInstance.stopLocalMedia();

      stopNetworkMonitor();

      if (ws) {
        ws.disconnect() && ws.clearBinds(wsBinds);
      }

      // Markapp as unloaded
      setState((s) => ({ ...s, fullAppLoad: false }));
    };
  }, []);

  // Change PA Queues Bind
  useEffect(() => {
    let bind: any;

    if (ws && userInfo) {
      bind = ws?.on("CHANGE_PA_QUEUES", onWsChangePaQueus);
      wsBinds.push(bind);
    }

    return () => bind && ws?.clearBinds([bind]);
  }, [userInfo, ws]);

  return (
    <main className={`page sf-main-wrapper text-sm flex flex-col h-screen ${appliedTheme} ${inScreenCall && "in-call"}`}>
      {state.fullAppLoad ? (
        <div className="flex-row flex grow">
          <Sidebar />
          <section className="flex w-full flex-col h-[100vh] relative">
            <Outlet />
          </section>
          <Aside />
        </div>
      ) : (
        <ScreenLoader />
      )}

      <audio id="remote-dialer-audio">
        <p>Your browser doesn't support HTML5 audio.</p>
      </audio>
    </main>
  );
}
